/**
 * Utilitats de sanejament i gestió de configuració de sincronització (SyncConfig)
 *
 * Garanteix:
 * 1. No inventar ni instanciar estructures niades buides no existents (Non-Destructive Payloads).
 * 2. Integritat de filtres ListValorsConfig (cap filtre amb llista buida pot tenir enabled: true).
 * 3. Preservació de camps i flags crítics (com adapters.*.enabled).
 * 4. Sanejament estricte abans d'enviar el payload al backend (PUT / POST).
 */

/**
 * Neteja una llista de valors, eliminant elements nuls, buits o d'espais en blanc.
 * @param {Array|string|null|undefined} rawList
 * @returns {string[]} Llista sanejada de cadenes no buides
 */
export const cleanListValors = (rawList) => {
  if (!rawList) return [];
  const arr = Array.isArray(rawList)
    ? rawList
    : (typeof rawList === 'string' && rawList.trim() ? [rawList.trim()] : []);

  return arr
    .map((item) => (typeof item === 'string' ? item.trim() : String(item || '').trim()))
    .filter((item) => item.length > 0);
};

/**
 * Saneja un bloc ListValorsConfig individual (compte, targeta, moviment...).
 * Si la llista és buida:
 * - Si no existia a initialFilter -> retorna null (per eliminar el bloc).
 * - Si existia prèviament -> retorna el filtre amb enabled: false i list-valors: [].
 * Si la llista té elements:
 * - Retorna el filtre amb enabled: true (o el valor que tingués si era explícit) i list-valors neta.
 *
 * @param {Object|null|undefined} currentFilter
 * @param {Object|null|undefined} initialFilter
 * @returns {Object|null} Filtre sanejat o null si s'ha d'ometre
 */
export const sanitizeListValorsFilter = (currentFilter, initialFilter) => {
  if (!currentFilter || typeof currentFilter !== 'object') {
    if (initialFilter && typeof initialFilter === 'object') {
      const cleanInit = cleanListValors(initialFilter['list-valors'] ?? initialFilter.listValors);
      if (cleanInit.length === 0) {
        return {
          ...initialFilter,
          enabled: false,
          'list-valors': []
        };
      }
      return {
        ...initialFilter,
        enabled: initialFilter.enabled !== false,
        'list-valors': cleanInit
      };
    }
    return null;
  }

  const rawList = currentFilter['list-valors'] ?? currentFilter.listValors;
  const cleanList = cleanListValors(rawList);

  if (cleanList.length > 0) {
    const res = {
      ...currentFilter,
      enabled: currentFilter.enabled !== false,
      'list-valors': cleanList
    };
    if ('listValors' in res) delete res.listValors;
    return res;
  }

  // Llista buida
  if (!initialFilter) {
    // No existia a la configuració original: ometre completament
    return null;
  }

  // Existia a la configuració original: STRICTAMENT enabled: false
  const res = {
    ...initialFilter,
    ...currentFilter,
    enabled: false,
    'list-valors': []
  };
  if ('listValors' in res) delete res.listValors;
  return res;
};

/**
 * Saneja un grup de filtres (ex: filtres.incloure, filtres.excloure, access-detall).
 *
 * @param {Object|null|undefined} currentGroup
 * @param {Object|null|undefined} initialGroup
 * @returns {Object|null} Grup sanejat o null si s'ha d'eliminar completament
 */
export const sanitizeTargetGroup = (currentGroup, initialGroup) => {
  const allKeys = Array.from(
    new Set([
      ...Object.keys(currentGroup || {}),
      ...Object.keys(initialGroup || {})
    ])
  );

  if (allKeys.length === 0) {
    return initialGroup && typeof initialGroup === 'object' ? {} : null;
  }

  const resultGroup = {};

  for (const key of allKeys) {
    const currTarget = currentGroup ? currentGroup[key] : undefined;
    const initTarget = initialGroup ? initialGroup[key] : undefined;

    const sanitized = sanitizeListValorsFilter(currTarget, initTarget);
    if (sanitized !== null) {
      resultGroup[key] = sanitized;
    }
  }

  if (Object.keys(resultGroup).length === 0) {
    return initialGroup && typeof initialGroup === 'object' ? {} : null;
  }

  return resultGroup;
};

/**
 * Helper per actualitzar un ListValorsConfig dins d'un adaptador (excloure.moviment, access-detall.moviment...)
 * evitant fabricar estructures niades buides i mantenint la integritat d'activació.
 *
 * @param {Object} currentAdapter
 * @param {Object} initialAdapter
 * @param {'excloure'|'access-detall'} targetSection
 * @param {string} targetKey ('moviment', 'compte', 'targeta')
 * @param {string[]} newItems
 * @returns {Object} Nou adaptador actualitzat
 */
export const updateAdapterListFilter = (
  currentAdapter = {},
  initialAdapter = {},
  targetSection,
  targetKey,
  newItems = []
) => {
  const cleanList = cleanListValors(newItems);
  const updatedAdapter = { ...currentAdapter };

  if (targetSection === 'excloure' || targetSection === 'incloure') {
    const currFiltres = currentAdapter.filtres;
    const initFiltres = initialAdapter?.filtres;
    const currGroup = currFiltres?.[targetSection];
    const initGroup = initFiltres?.[targetSection];
    const initTargetExists = Boolean(initGroup?.[targetKey]);

    if (cleanList.length > 0) {
      updatedAdapter.filtres = {
        ...(currFiltres || {}),
        [targetSection]: {
          ...(currGroup || {}),
          [targetKey]: {
            ...(currGroup?.[targetKey] || {}),
            enabled: true,
            'list-valors': cleanList
          }
        }
      };
      if ('listValors' in (updatedAdapter.filtres[targetSection][targetKey] || {})) {
        delete updatedAdapter.filtres[targetSection][targetKey].listValors;
      }
    } else {
      // Llista buida
      if (!initTargetExists) {
        // No existia a l'original: netejar sense deixar blocs buits fabricats
        if (updatedAdapter.filtres?.[targetSection]) {
          const newGroup = { ...updatedAdapter.filtres[targetSection] };
          delete newGroup[targetKey];

          if (Object.keys(newGroup).length === 0 && !initGroup) {
            const newFiltres = { ...updatedAdapter.filtres };
            delete newFiltres[targetSection];
            if (Object.keys(newFiltres).length === 0 && !initFiltres) {
              delete updatedAdapter.filtres;
            } else {
              updatedAdapter.filtres = newFiltres;
            }
          } else {
            updatedAdapter.filtres = {
              ...updatedAdapter.filtres,
              [targetSection]: newGroup
            };
          }
        }
      } else {
        // Existia a l'original: strictly enabled: false
        updatedAdapter.filtres = {
          ...(currFiltres || {}),
          [targetSection]: {
            ...(currGroup || {}),
            [targetKey]: {
              ...(currGroup?.[targetKey] || {}),
              enabled: false,
              'list-valors': []
            }
          }
        };
        if ('listValors' in (updatedAdapter.filtres[targetSection][targetKey] || {})) {
          delete updatedAdapter.filtres[targetSection][targetKey].listValors;
        }
      }
    }
  } else if (targetSection === 'access-detall') {
    const adKey =
      'access-detall' in currentAdapter
        ? 'access-detall'
        : 'accessDetall' in currentAdapter
        ? 'accessDetall'
        : 'access-detall';

    const currAd = currentAdapter[adKey];
    const initAd = initialAdapter?.['access-detall'] || initialAdapter?.accessDetall;
    const initTargetExists = Boolean(initAd?.[targetKey]);

    if (cleanList.length > 0) {
      updatedAdapter[adKey] = {
        ...(currAd || {}),
        [targetKey]: {
          ...(currAd?.[targetKey] || {}),
          enabled: true,
          'list-valors': cleanList
        }
      };
      if ('listValors' in (updatedAdapter[adKey][targetKey] || {})) {
        delete updatedAdapter[adKey][targetKey].listValors;
      }
    } else {
      if (!initTargetExists) {
        if (updatedAdapter[adKey]) {
          const newAd = { ...updatedAdapter[adKey] };
          delete newAd[targetKey];
          if (Object.keys(newAd).length === 0 && !initAd) {
            delete updatedAdapter[adKey];
            delete updatedAdapter['access-detall'];
            delete updatedAdapter.accessDetall;
          } else {
            updatedAdapter[adKey] = newAd;
          }
        }
      } else {
        updatedAdapter[adKey] = {
          ...(currAd || {}),
          [targetKey]: {
            ...(currAd?.[targetKey] || {}),
            enabled: false,
            'list-valors': []
          }
        };
        if ('listValors' in (updatedAdapter[adKey][targetKey] || {})) {
          delete updatedAdapter[adKey][targetKey].listValors;
        }
      }
    }
  }

  return updatedAdapter;
};

/**
 * Escaneig recursiu per garantir que cap objecte de tipus ListValorsConfig
 * tingui enabled: true amb list-valors buida o nul·la.
 *
 * @param {any} node
 */
const enforceListValorsIntegrityRecursively = (node) => {
  if (!node || typeof node !== 'object') return;

  if (Array.isArray(node)) {
    node.forEach(enforceListValorsIntegrityRecursively);
    return;
  }

  if ('list-valors' in node || 'listValors' in node) {
    const clean = cleanListValors(node['list-valors'] ?? node.listValors);
    if (clean.length === 0) {
      node.enabled = false;
      node['list-valors'] = [];
    } else {
      node['list-valors'] = clean;
      if (node.enabled === undefined) {
        node.enabled = true;
      }
    }
    if ('listValors' in node) {
      delete node.listValors;
    }
  }

  for (const key of Object.keys(node)) {
    enforceListValorsIntegrityRecursively(node[key]);
  }
};

/**
 * Rutina principal de sanejament de payload de SyncConfig abans d'enviar al backend.
 *
 * @param {Object} currentConfig Configuració actual amb els canvis de l'usuari
 * @param {Object} initialConfig Configuració original rebuda del backend
 * @returns {Object} Payload sanejat llest per enviar (PUT/POST)
 */
export const sanitizeSyncConfigPayload = (currentConfig, initialConfig) => {
  if (!currentConfig || typeof currentConfig !== 'object') {
    return currentConfig;
  }

  // 1. Clonant per evitar mutacions col·laterals
  const payload = JSON.parse(JSON.stringify(currentConfig));
  const initial = initialConfig && typeof initialConfig === 'object'
    ? JSON.parse(JSON.stringify(initialConfig))
    : {};

  // 2. Preservar activació global del perfil
  if (payload.enabled === undefined && initial.enabled !== undefined) {
    payload.enabled = initial.enabled;
  }

  // 3. Sanejament d'adaptadors bancaris
  if (payload.adapters && typeof payload.adapters === 'object') {
    const initialAdapters = initial.adapters || {};
    const adapterKeys = Object.keys(payload.adapters);

    for (const aKey of adapterKeys) {
      const adapter = payload.adapters[aKey];
      if (!adapter || typeof adapter !== 'object') continue;
      const initAdapter = initialAdapters[aKey] || null;

      // Preservar activació de l'adaptador (Requirement 3: no passar actius a false inadvertidament)
      if (adapter.enabled === undefined && initAdapter?.enabled !== undefined) {
        adapter.enabled = initAdapter.enabled;
      }
      if (initAdapter?.enabled === true && adapter.enabled === undefined) {
        adapter.enabled = true;
      }

      // Sanejament de 'filtres'
      const currFiltres = adapter.filtres;
      const initFiltres = initAdapter?.filtres;

      if (currFiltres || initFiltres) {
        const filterTypes = Array.from(
          new Set([
            ...Object.keys(currFiltres || {}),
            ...Object.keys(initFiltres || {})
          ])
        );

        const sanitizedFiltres = {};

        for (const fType of filterTypes) {
          const currGroup = currFiltres ? currFiltres[fType] : undefined;
          const initGroup = initFiltres ? initFiltres[fType] : undefined;

          const cleanGroup = sanitizeTargetGroup(currGroup, initGroup);

          if (cleanGroup !== null && Object.keys(cleanGroup).length > 0) {
            sanitizedFiltres[fType] = cleanGroup;
          } else if (initGroup && Object.keys(initGroup).length === 0) {
            sanitizedFiltres[fType] = {};
          }
        }

        if (Object.keys(sanitizedFiltres).length > 0) {
          adapter.filtres = sanitizedFiltres;
        } else if (initFiltres && Object.keys(initFiltres).length === 0) {
          adapter.filtres = {};
        } else {
          delete adapter.filtres;
        }
      } else {
        delete adapter.filtres;
      }

      // Sanejament de 'access-detall' i 'accessDetall'
      const currAdKey =
        'access-detall' in adapter
          ? 'access-detall'
          : 'accessDetall' in adapter
          ? 'accessDetall'
          : 'access-detall';

      const currAd = adapter[currAdKey];
      const initAd = initAdapter?.['access-detall'] || initAdapter?.accessDetall;

      if (currAd || initAd) {
        const cleanAd = sanitizeTargetGroup(currAd, initAd);

        if (cleanAd !== null && Object.keys(cleanAd).length > 0) {
          adapter[currAdKey] = cleanAd;
          if (currAdKey === 'access-detall') delete adapter.accessDetall;
          if (currAdKey === 'accessDetall') delete adapter['access-detall'];
        } else if (initAd && Object.keys(initAd).length === 0) {
          adapter[currAdKey] = {};
        } else {
          delete adapter['access-detall'];
          delete adapter.accessDetall;
        }
      } else {
        delete adapter['access-detall'];
        delete adapter.accessDetall;
      }
    }
  }

  // 4. Passada recursiva de seguretat sobre tot el payload per garantir integritat de ListValorsConfig
  enforceListValorsIntegrityRecursively(payload);

  return payload;
};

/**
 * Detecta si la configuració conté algun filtre ListValorsConfig amb enabled: true
 * però amb la llista de valors buida (la incidència descrita pel backend).
 *
 * @param {any} node
 * @param {string} currentPath
 * @returns {Array<{path: string, item: any}>} Llista d'elements corruptes detectats
 */
export const findCorruptedEmptyFilters = (node, currentPath = '') => {
  const corrupted = [];
  if (!node || typeof node !== 'object') return corrupted;

  if ('list-valors' in node || 'listValors' in node) {
    const clean = cleanListValors(node['list-valors'] ?? node.listValors);
    if (clean.length === 0 && node.enabled === true) {
      corrupted.push({ path: currentPath || 'filtre', item: node });
    }
  }

  for (const key of Object.keys(node)) {
    const subPath = currentPath ? `${currentPath}.${key}` : key;
    corrupted.push(...findCorruptedEmptyFilters(node[key], subPath));
  }

  return corrupted;
};

/**
 * Retorna true si la configuració conté almenys un filtre corrupte (enabled: true amb llista buida).
 *
 * @param {Object} config
 * @returns {boolean}
 */
export const hasCorruptedEmptyFilters = (config) => {
  return findCorruptedEmptyFilters(config).length > 0;
};

